// MACHINERY V2 (Stroje) — the hero's identity field rows (M2.4).
//
// Spec: MACHINE_PROFILE_REDESIGN_DATA_PACKAGE.md §2 (Identity Sidebar Card) —
// BINDING. Live source of truth: components/ProfileSidebarCard.tsx.
//
// >>> WHY THESE FIELDS LIVE IN THE HERO AND NOT IN A SIDEBAR. <<<
// Layout C (machinery-profile-mash.jsx, artboard 0504) has NO left identity
// column: it folds identity into a full-width hero, and 0505 states the intent
// verbatim — "Hero proportions match the V2 sidebar's content, just rotated."
// Manager ruling 2026-08-22: THE DESIGNER LAYOUT WINS ON STRUCTURE (hero only,
// no sidebar), while §2 FIELD parity stays FULL — the look/fields split. So
// every field the sidebar card binds is bound HERE, in the rotated form:
//   chips row (mono identity strings) + location row + KV grid (spec values)
//   + pills (taper / groups / catalog badge).
// Health dot, decommissioned badge, name, subtitle and brand hierarchy sit one
// level up in ProfileHero (they belong to the name block, not the field rows).
//
// PER-FIELD GUARDS ARE PARITY, NOT POLISH (§2 "States"): every row self-hides
// when empty — EXCEPT Location, the one always-rendered row, which falls back
// to an italic "Location not set". The KV atom's own "—" placeholder is
// therefore deliberately never reached here.
//
// EDGE CASE CARRIED VERBATIM (§2): if `controlSystemBrand.name` is falsy but
// `system` is set, the ENTIRE System row is hidden — including the system text.
// That is the live behaviour; do not "fix" it here.
//
// Derived strings come from the shared helpers (getBrandDisplayName /
// getProductionYearDisplay) — never re-derived (data package §0 rule 3).
//
// Tooltips use the native `title` attribute, exactly as the M2.2 HealthDot atom
// does — a Radix tooltip would portal outside `.machinery-v2-root` and need the
// scope-class countermeasure for a two-word hint.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import {
  getBrandDisplayName,
  getProductionYearDisplay,
} from "../../../../shared/utils/machineUtils";
import { Machine } from "../../models/models";
import { KV, PinGlyph, groupColor } from "../fleet/fleetCardAtoms";

const chipStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  height: 22,
  padding: "0 9px",
  borderRadius: 6,
  border: "1px solid var(--border)",
  background: "var(--bg-soft)",
  fontFamily: "var(--font-mono)",
  fontSize: 10.5,
  color: "var(--muted)",
  letterSpacing: "0.02em",
  whiteSpace: "nowrap",
  maxWidth: "100%",
  minWidth: 0,
};

const chipLabel: React.CSSProperties = { color: "var(--faint)" };

const chipValue: React.CSSProperties = {
  color: "var(--ink-2)",
  fontWeight: 600,
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const MonoChip: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <span style={chipStyle}>
    <span style={chipLabel}>{label}</span>
    <span style={chipValue}>{value}</span>
  </span>
);

/** THE one always-rendered row (§2). Accent-tinted, pin glyph, `parent > site`. */
const LocationChip: React.FC<{ machine: Machine }> = ({ machine }) => {
  const { translate } = useTranslation("myMachinery");
  const isSet = !!machine.locationName;
  const text = isSet
    ? machine.parentLocationName
      ? machine.parentLocationName + " › " + machine.locationName
      : machine.locationName
    : translate("myMachinery:locationNotSet");

  return (
    <span
      style={{
        ...chipStyle,
        border: "1px solid var(--mod-accent-line)",
        background: "var(--mod-accent-soft)",
      }}
    >
      <span style={{ color: "var(--mod-accent)", display: "inline-flex" }}>
        <PinGlyph />
      </span>
      <span
        style={{
          ...chipValue,
          fontWeight: isSet ? 600 : 400,
          fontStyle: isSet ? "normal" : "italic",
          color: isSet ? "var(--ink-2)" : "var(--faint)",
        }}
      >
        {text}
      </span>
    </span>
  );
};

export interface ProfileIdentityRowsProps {
  machine: Machine;
  /**
   * Suppress the KATALÓG chip because the HOST already shows it (M2.16d review,
   * item 2).
   *
   * Set by ProfileHeroPhone alone: the below-1280 band paints board 20/21's
   * KATALÓG pill in its top-right cluster AND mounts this component inside its
   * details disclosure, both gated on `machine.catalogModelId` and both
   * translated from `myMachinery:catalogModel` — so expanding the disclosure
   * printed the same pill twice, about 120px apart.
   *
   * IT IS THE CHIP THAT YIELDS, NOT THE BAND'S PILL: the band's is always
   * visible and the board draws it there, while this row is behind a collapsed
   * disclosure. Omitted — the desktop hero — is §2 field parity, unchanged, and
   * no §2 FACT is lost either way, because the fact is on screen above.
   */
  omitCatalogPill?: boolean;
}

const ProfileIdentityRows: React.FC<ProfileIdentityRowsProps> = ({
  machine,
  omitCatalogPill,
}) => {
  const { translate } = useTranslation("myMachinery");

  const brandName = getBrandDisplayName(machine);
  const yearDisplay = getProductionYearDisplay(machine);
  const groups = machine.groupNames || [];
  const systemBrand = machine.controlSystemBrand?.name;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
      {/* Identity strings + the always-on location row. */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, minWidth: 0 }}>
        {machine.serialNumber && (
          <MonoChip label={translate("myMachinery:sn")} value={machine.serialNumber} />
        )}
        {machine.internalNumber && (
          <MonoChip
            label={translate("myMachinery:machineNo")}
            value={machine.internalNumber}
          />
        )}
        <LocationChip machine={machine} />
        {machine.taperType?.name && (
          <span
            title={
              machine.taperType.standard
                ? machine.taperType.name + " - " + machine.taperType.standard
                : machine.taperType.name
            }
            style={{
              ...chipStyle,
              border: "1px solid rgba(142, 68, 173, 0.28)",
              background: "rgba(142, 68, 173, 0.08)",
              fontWeight: 600,
            }}
          >
            <span style={{ ...chipLabel, color: "#8E44AD", opacity: 0.7 }}>
              {translate("myMachinery:taperType")}
            </span>
            <span style={{ ...chipValue, color: "#8E44AD" }}>{machine.taperType.name}</span>
          </span>
        )}
        {machine.catalogModelId && !omitCatalogPill && (
          <span
            style={{
              ...chipStyle,
              borderRadius: 999,
              border: "1px solid rgba(52, 152, 219, 0.34)",
              background: "rgba(52, 152, 219, 0.10)",
              color: "#3498DB",
              fontWeight: 700,
              fontSize: 10,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
            }}
          >
            {translate("myMachinery:catalogModel")}
          </span>
        )}
        {groups.map((name: string) => (
          <span
            key={name}
            style={{
              ...chipStyle,
              borderRadius: 999,
              fontFamily: "inherit",
              fontSize: 10.5,
              fontWeight: 500,
              border: "1px solid " + groupColor(name) + "33",
              background: groupColor(name) + "14",
              color: groupColor(name),
            }}
          >
            {name}
          </span>
        ))}
      </div>

      {/* Spec values. Each cell self-hides when its field is empty (§2). */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(126px, 1fr))",
          rowGap: 8,
          columnGap: 18,
          minWidth: 0,
        }}
      >
        {brandName && <KV label={translate("myMachinery:brand")} value={brandName} />}
        {machine.type?.name && (
          <KV label={translate("myMachinery:type")} value={machine.type.name.toUpperCase()} />
        )}
        {systemBrand && (
          <KV
            label={translate("myMachinery:system")}
            value={systemBrand}
            sub={machine.system}
          />
        )}
        {yearDisplay && (
          <KV label={translate("myMachinery:productionYear")} value={yearDisplay} mono />
        )}
        {!!machine.maxSpindleRpm && (
          <KV
            label={translate("myMachinery:maxSpindleRpm")}
            value={machine.maxSpindleRpm.toLocaleString() + " RPM"}
            mono
          />
        )}
        {!!machine.spindleHp && (
          <KV
            label={translate("myMachinery:spindleHp")}
            value={machine.spindleHp + " HP"}
            mono
          />
        )}
        {!!machine.toolCapacity && (
          <KV
            label={translate("myMachinery:toolCapacity")}
            value={machine.toolCapacity.toString()}
            mono
          />
        )}
      </div>
    </div>
  );
};

export default ProfileIdentityRows;
